import * as React from 'react';
import { Link } from 'react-router-dom';
import { SimpleModal } from '../../components/SimpleModal/SimpleModal';
import './SignupLanding.scss';

type IconDefinition = string[];

type Feature = {
  key: string;
  name: string;
  icon: IconDefinition;
  blurb: string;
  bullets: string[];
};

type StrokeIconProps = {
  paths: IconDefinition;
  size?: number;
  strokeWidth?: number;
  className?: string;
};

const ICONS: { [key: string]: IconDefinition } = {
  arrow: ['M5 12h14', 'M13 6l6 6-6 6'],
  sun: [
    'M12 3v3',
    'M12 18v3',
    'M5.6 5.6l2.1 2.1',
    'M16.3 16.3l2.1 2.1',
    'M3 12h3',
    'M18 12h3',
    'M5.6 18.4l2.1-2.1',
    'M16.3 7.7l2.1-2.1'
  ],
  school: ['M3 21h18', 'M5 21V10l7-5 7 5v11', 'M9 21v-6h6v6'],
  student: ['M22 10 12 5 2 10l10 5 10-5z', 'M6 12.5V18c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-5.5'],
  parent: [
    'M17 21v-2a4 4 0 0 0-3-3.9',
    'M9 21v-2a4 4 0 0 1 3-3.9',
    'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
    'M2 21v-2a4 4 0 0 1 3-3.9'
  ],
  staff: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M19 8v6', 'M16 11h6'],
  comms: ['M3 11 21 4l-7 17-3-7-8-3z', 'M11 14l4-4'],
  fixtures: [
    'M4 5h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
    'M3 10h18',
    'M8 3v4',
    'M16 3v4',
    'M8 15h4'
  ],
  clubs: ['M9 11l3 3L22 4', 'M21 12v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h11'],
  awards: ['M8 21h8', 'M12 17v4', 'M7 4h10v5a5 5 0 0 1-10 0V4z', 'M17 5h3v2a3 3 0 0 1-3 3', 'M7 5H4v2a3 3 0 0 0 3 3'],
  associationTournament: ['M4 20h16', 'M7 20v-4', 'M12 20v-8', 'M17 20v-12'],
  schoolTournament: ['M5 21V4', 'M5 5h12l-2.5 4L17 13H5']
};

const FEATURES: Feature[] = [
  {
    key: 'comms',
    name: 'Comms',
    icon: ICONS.comms,
    blurb: 'Announcements, targeted email and push, emergency alerts, and audience lists that build themselves.',
    bullets: [
      'Send to parents, students and staff in one message',
      'Build audiences from teams, clubs, houses and year groups',
      'Add attachments, schedule delivery and collect digital consent'
    ]
  },
  {
    key: 'fixtures',
    name: 'Fixtures and results',
    icon: ICONS.fixtures,
    blurb: 'Plan fixtures, publish teams, collect availability, sort transport, record results and match reports.',
    bullets: [
      'Publish once and keep every audience up to date',
      'Manage team selection, availability and travel notes',
      'Record results, tables and match reports'
    ]
  },
  {
    key: 'clubs',
    name: 'Clubs and activities',
    icon: ICONS.clubs,
    blurb: 'Open a season, take sign-ups, keep registers and run waiting lists when a club fills up.',
    bullets: [
      'Run online sign-up and consent',
      'Keep attendance registers and safeguarding notes',
      'Publish changes and booking updates'
    ]
  },
  {
    key: 'awards',
    name: 'Awards and house points',
    icon: ICONS.awards,
    blurb: 'Award achievement, run the house leaderboard, and let parents see it as it happens.',
    bullets: [
      'Create award sets and point values',
      'Run a house leaderboard using your school colours',
      'Keep parents informed about achievement'
    ]
  },
  {
    key: 'tournaments_assoc',
    name: 'Tournaments for associations',
    icon: ICONS.associationTournament,
    blurb: 'Run multi-school competitions end to end: entries, eligibility, live results, comms and reporting.',
    bullets: [
      'Take online entries with eligibility checks and waiting lists',
      'Publish live results to the web and app',
      'Create entry, participation, result and award reports'
    ]
  },
  {
    key: 'tournaments_school',
    name: 'Tournaments for schools',
    icon: ICONS.schoolTournament,
    blurb: 'Enter squads in a few taps, keep teams current, hear about changes instantly and see published results.',
    bullets: [
      'Complete online entries and update squads',
      'Receive immediate time and venue changes',
      'View published results and downloadable reports'
    ]
  }
];

const FAQS = [
  {
    question: 'Is the 30-day trial really full access?',
    answer:
      'Yes. Every module is switched on, with no cap on staff, pupils or messages. Nothing is held back for the paid plans.'
  },
  {
    question: 'Do parents or students pay?',
    answer: 'No. Parent and student accounts are always free. The school holds the subscription.'
  },
  {
    question: 'Can we start without IT support?',
    answer:
      'Yes. Sign up with an email address and invite colleagues from inside the platform. There is nothing to install and no server to configure.'
  },
  {
    question: 'Can associations run entries and results?',
    answer:
      'Yes. Associations publish a tournament, take entries from member schools, check eligibility, and publish live results that every school sees in its own account.'
  }
];

const CALENDLY_URL = 'https://calendly.com/squadintouch';
const GUIDES_URL = 'https://www1.squadintouch.com/support-guides/';
const DATA_PROTECTION_URL = 'https://www.squadintouch.com/protection/';
const SCHOOL_TRIAL_URL = '/register?role=staff&firstStaff=true';

function StrokeIcon({ paths, size = 22, strokeWidth = 1.8, className }: StrokeIconProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((path, index) => (
        <path d={path} key={`${path}-${index}`} />
      ))}
    </svg>
  );
}

export default function SignUpLanding(): JSX.Element {
  const [openFeatureKey, setOpenFeatureKey] = React.useState<string | null>(null);
  const activeFeature = FEATURES.find(feature => feature.key === openFeatureKey) || null;

  return (
    <div className="signupLanding sit-scroll">
      <header className="signupLanding__header">
        <div className="signupLanding__headerInner">
          <Link className="signupLanding__brand" to="/login" aria-label="Squad In Touch sign in">
            <img src="/dist/images/logoImage.png" alt="" />
            <span>Squad In Touch</span>
          </Link>
          <nav className="signupLanding__headerActions" aria-label="Account actions">
            <Link className="signupLanding__signIn" to="/login">
              Sign in
            </Link>
            <Link
              className="signupLanding__button signupLanding__button--primary signupLanding__button--header"
              to={SCHOOL_TRIAL_URL}
            >
              Start free trial
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="signupLanding__heroSection">
          <div className="signupLanding__hero">
            <div className="signupLanding__heroCopy">
              <div className="signupLanding__trialBadge">
                <StrokeIcon paths={ICONS.sun} size={14} strokeWidth={2} />
                30-day full trial for new schools
              </div>
              <h1>One platform for school sport, activities, awards and parent comms</h1>
              <p>
                Squad In Touch connects staff, students and parents — fixtures, clubs, awards and tournaments, with
                instant messaging and digital consent built in.
              </p>
              <div className="signupLanding__heroActions">
                <Link className="signupLanding__button signupLanding__button--primary" to={SCHOOL_TRIAL_URL}>
                  Start your free trial
                  <StrokeIcon paths={ICONS.arrow} size={17} strokeWidth={2} />
                </Link>
                <a
                  className="signupLanding__button signupLanding__button--outline"
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a demo
                </a>
              </div>
              <div className="signupLanding__stats" aria-label="Squad In Touch in numbers">
                <div>
                  <strong>400+</strong>
                  <span>schools and associations</span>
                </div>
                <div>
                  <strong>10 years</strong>
                  <span>building for school sport</span>
                </div>
              </div>
            </div>

            <div className="signupLanding__week" aria-label="This week at Elmstead High">
              <div className="signupLanding__weekHeader">
                <span className="signupLanding__liveDot" />
                <strong>This week at Elmstead High</strong>
                <span>live</span>
              </div>
              <div className="signupLanding__weekRows">
                <div>
                  <span>U13A boys fixture — Saturday 10:00</span>
                  <em className="is-success">Confirmed</em>
                </div>
                <div>
                  <span>Chess club — 14 of 16 places filled</span>
                  <em className="is-info">Booking open</em>
                </div>
                <div>
                  <span>Hawkins House — 142 points</span>
                  <em className="is-warning">Leading</em>
                </div>
                <div>
                  <span>Ski trip consent — 18 of 18 parents</span>
                  <em className="is-success">All in</em>
                </div>
                <div>
                  <span>Kit reminder to 240 parents — Friday 16:00</span>
                  <em className="is-neutral">Scheduled</em>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="signupLanding__section signupLanding__section--roles">
          <div className="signupLanding__sectionInner">
            <div className="signupLanding__eyebrow">Where do you start</div>
            <h2>Set up your school, or join one</h2>
            <p className="signupLanding__sectionIntro">
              Schools set the platform up once. Everyone else joins the school that already uses it.
            </p>

            <div className="signupLanding__roles">
              <article className="signupLanding__role signupLanding__role--school">
                <span className="signupLanding__roleIcon">
                  <StrokeIcon paths={ICONS.school} size={19} />
                </span>
                <h3>Your school</h3>
                <div className="signupLanding__roleState">New to Squad In Touch</div>
                <p>
                  Set up a free account in minutes. Run the full platform for 30 days, then keep the essentials free or
                  pay only for the modules you use.
                </p>
                <div className="signupLanding__roleActions">
                  <Link className="signupLanding__button signupLanding__button--onNavy" to={SCHOOL_TRIAL_URL}>
                    Start 30-day trial
                    <StrokeIcon paths={ICONS.arrow} size={16} strokeWidth={2.1} />
                  </Link>
                  <a
                    className="signupLanding__button signupLanding__button--darkOutline"
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book a demo instead
                  </a>
                </div>
              </article>

              <article className="signupLanding__role">
                <span className="signupLanding__roleIcon">
                  <StrokeIcon paths={ICONS.student} size={19} />
                </span>
                <h3>Students</h3>
                <div className="signupLanding__roleState">You already have an account</div>
                <p>Your school created it for you. You only need to set a password.</p>
                <div className="signupLanding__roleActions">
                  <Link
                    className="signupLanding__button signupLanding__button--brandOutline"
                    to="/resetPassword?mode=setup&source=student"
                  >
                    Set your password
                  </Link>
                  <small>
                    Email not recognised? <a href="mailto:support@squadintouch.com">support@squadintouch.com</a>
                  </small>
                </div>
              </article>

              <article className="signupLanding__role">
                <span className="signupLanding__roleIcon">
                  <StrokeIcon paths={ICONS.parent} size={19} />
                </span>
                <h3>Parents and guardians</h3>
                <div className="signupLanding__roleState">Create your parent space</div>
                <p>
                  Follow your child's fixtures, clubs and awards, get told the moment something changes, and give
                  consent from your phone.
                </p>
                <div className="signupLanding__roleActions">
                  <Link
                    className="signupLanding__button signupLanding__button--brandOutline"
                    to="/register?role=parent"
                  >
                    Create account
                  </Link>
                </div>
              </article>

              <article className="signupLanding__role">
                <span className="signupLanding__roleIcon">
                  <StrokeIcon paths={ICONS.staff} size={19} />
                </span>
                <h3>School staff</h3>
                <div className="signupLanding__roleState">Your school is already set up</div>
                <p>Join your colleagues to run sport, activities, awards, the house race and parent comms.</p>
                <div className="signupLanding__roleActions">
                  <Link className="signupLanding__button signupLanding__button--brandOutline" to="/register?role=staff">
                    Join your school
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="signupLanding__section signupLanding__section--modules">
          <div className="signupLanding__sectionInner">
            <div className="signupLanding__eyebrow">Platform</div>
            <h2>Pick what you need — everything works together</h2>
            <div className="signupLanding__modules">
              {FEATURES.map(feature => (
                <article className="signupLanding__module" key={feature.key}>
                  <span className="signupLanding__moduleIcon">
                    <StrokeIcon paths={feature.icon} />
                  </span>
                  <h3>{feature.name}</h3>
                  <p>{feature.blurb}</p>
                  <button type="button" onClick={() => setOpenFeatureKey(feature.key)}>
                    Learn more <span aria-hidden="true">→</span>
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="signupLanding__section signupLanding__section--faq">
          <div className="signupLanding__faq">
            <div>
              <div className="signupLanding__eyebrow">Questions</div>
              <h2>Common questions</h2>
              <div className="signupLanding__questions">
                {FAQS.map(item => (
                  <details key={item.question}>
                    <summary>
                      <span>{item.question}</span>
                      <span className="signupLanding__questionPlus" aria-hidden="true">
                        +
                      </span>
                    </summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <aside className="signupLanding__help">
              <h3>Need a hand?</h3>
              <p>We usually reply within one working day, and the people replying know the platform inside out.</p>
              <div>
                <a
                  className="signupLanding__button signupLanding__button--primary"
                  href="mailto:support@squadintouch.com"
                >
                  Email support
                </a>
                <a
                  className="signupLanding__button signupLanding__button--outline"
                  href={GUIDES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  User guides
                </a>
                <a
                  className="signupLanding__button signupLanding__button--outline"
                  href={DATA_PROTECTION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Data protection
                </a>
              </div>
            </aside>
          </div>
        </section>

        <section className="signupLanding__closing">
          <div className="signupLanding__closingInner">
            <div>
              <h2>Try it with next term's fixtures</h2>
              <p>
                Thirty days, every module, no card. Set your school up this afternoon and see how it holds a real week.
              </p>
            </div>
            <div className="signupLanding__closingActions">
              <Link className="signupLanding__button signupLanding__button--onNavy" to={SCHOOL_TRIAL_URL}>
                Start your free trial
                <StrokeIcon paths={ICONS.arrow} size={17} strokeWidth={2.1} />
              </Link>
              <a
                className="signupLanding__button signupLanding__button--darkOutline"
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a demo
              </a>
            </div>
          </div>
        </section>
      </main>

      {activeFeature && (
        <SimpleModal
          isOpen={true}
          title={
            <span className="signupLandingFeatureModal__title">
              <StrokeIcon paths={activeFeature.icon} size={20} />
              {activeFeature.name}
            </span>
          }
          body={
            <div className="signupLandingFeatureModal__body">
              <p>{activeFeature.blurb}</p>
              <ul>
                {activeFeature.bullets.map(bullet => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              {activeFeature.key === 'tournaments_assoc' && (
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  Book a demo
                </a>
              )}
            </div>
          }
          buttonCancelText="Close"
          onCloseClick={() => setOpenFeatureKey(null)}
          customClass="signupLandingFeatureModal"
          dialogStyle={{ maxWidth: 560 }}
          isBodyBold={false}
        />
      )}
    </div>
  );
}
